import type { ActivityDetail, StreamsPayload } from '@painpal/contracts';
import { formatPace, gradeAdjustedSpeeds, paceOf } from '@painpal/core';
import type { DataZoomComponentOption } from 'echarts/components';
import { AXIS_TEXT, chartTooltip, pointValue } from '../../lib/chartTheme';
import type { ECOption, EChartsType, LineSeriesOption } from '../../lib/echarts';
import { formatHoursMinutes, formatMinutesSeconds, formatPaceKm, orDash } from '../../lib/format';
import { alpha, colors } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';

export const STREAMS_GROUP = 'activity-streams';

export interface TrackSpec {
  kind: 'power' | 'hr' | 'speed' | 'elevation' | 'pace';
  name: string;
  unit: string;
  stream: 'power' | 'hr' | 'speed' | 'altitude';
  scale: number;
  decimals: number;
  line: string;
  area: string | null;
  height: number;
  phoneHeight: number;
}

export const TRACK_SPECS: readonly TrackSpec[] = [
  { kind: 'power', name: 'POWER', unit: 'W', stream: 'power', scale: 1, decimals: 0, line: colors.ink, area: alpha.powerArea, height: 96, phoneHeight: 80 },
  { kind: 'hr', name: 'HEART RATE', unit: 'BPM', stream: 'hr', scale: 1, decimals: 0, line: colors.hot, area: null, height: 72, phoneHeight: 60 },
  { kind: 'speed', name: 'SPEED', unit: 'KM/H', stream: 'speed', scale: 3.6, decimals: 1, line: colors.ink, area: null, height: 72, phoneHeight: 60 },
  { kind: 'elevation', name: 'ELEVATION', unit: 'M', stream: 'altitude', scale: 1, decimals: 0, line: colors.ink, area: alpha.elevationArea, height: 72, phoneHeight: 60 },
];

export type Point = [number, number | null];
// gap and threshold are the pace track's grade-adjusted line and threshold pace line.
export interface TrackData { spec: TrackSpec; points: Point[]; avg: number | null; max: number | null; note: string; gap?: Point[]; threshold?: number | null }

export function trackStats(values: (number | null)[]): { avg: number | null; max: number | null } {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return { avg: null, max: null };
  return { avg: present.reduce((sum, v) => sum + v, 0) / present.length, max: Math.max(...present) };
}

function scaled(value: number | null | undefined, spec: TrackSpec): number | null {
  if (value === null || value === undefined) return null;
  return Number((value * spec.scale).toFixed(spec.decimals));
}

export function buildTrack(streams: StreamsPayload, spec: TrackSpec): TrackData | null {
  const raw = streams[spec.stream];
  if (raw === undefined) return null;
  const points = streams.t.map((t, i): Point => [t, scaled(raw[i], spec)]);
  const stats = trackStats(points.map((p) => p[1]));
  return stats.avg === null ? null : { spec, points, ...stats, note: `AVG · MAX ${formatTrackValue(stats.max, spec)}` };
}

export function buildTracks(streams: StreamsPayload): TrackData[] {
  return TRACK_SPECS.map((spec) => buildTrack(streams, spec)).filter((t): t is TrackData => t !== null);
}

export const PACE_SPEC: TrackSpec = {
  kind: 'pace', name: 'PACE', unit: '/KM', stream: 'speed', scale: 1, decimals: 0, line: colors.ink, area: null, height: 96, phoneHeight: 80,
};

// Slower than 1 m/s (16:40 /km) is a stop: its pace would flatten every real effort on the chart.
const MIN_PACE_SPEED = 1;
const toPace = (v: number | null | undefined): number | null => (v === null || v === undefined || v < MIN_PACE_SPEED ? null : Math.round(paceOf(v)));

export function buildPaceTrack(streams: StreamsPayload, avgS: number | null, gapS: number | null, thresholdS: number | null): TrackData | null {
  const { speed } = streams;
  if (speed === undefined) return null;
  const points = streams.t.map((t, i): Point => [t, toPace(speed[i])]);
  if (points.every((p) => p[1] === null)) return null;
  const adjusted = gradeAdjustedSpeeds(streams);
  return {
    spec: PACE_SPEC, points, gap: streams.t.map((t, i): Point => [t, toPace(adjusted[i])]), threshold: thresholdS,
    avg: avgS, max: null, note: gapS === null ? 'AVG' : `AVG · GAP ${formatPaceKm(gapS)}`,
  };
}

// A run's tracks: pace with its grade-adjusted line, then heart rate and elevation when present.
export function runTracks(streams: StreamsPayload, a: ActivityDetail): TrackData[] {
  const avgS = a.distanceM === null || a.distanceM === 0 ? null : Math.round((a.movingS * 1000) / a.distanceM);
  const gapS = a.weightedSpeed === null ? null : Math.round(paceOf(a.weightedSpeed));
  const thresholdS = a.thresholdUsed?.kind === 'threshold_pace' ? a.thresholdUsed.value : null;
  const others = TRACK_SPECS.filter((s) => s.kind === 'hr' || s.kind === 'elevation').map((s) => buildTrack(streams, s));
  return [buildPaceTrack(streams, avgS, gapS, thresholdS), ...others].filter((t): t is TrackData => t !== null);
}

function seriesValue(series: unknown): number | null {
  if (typeof series !== 'object' || series === null || !('value' in series) || !Array.isArray(series.value)) return null;
  const value: unknown = series.value[1];
  return typeof value === 'number' ? value : null;
}

export function paceTooltip(params: unknown): string {
  const [pace, gap] = Array.isArray(params) ? params : [params];
  const paceValue = seriesValue(pace);
  if (paceValue === null) return '—';
  const gapValue = seriesValue(gap);
  return gapValue === null ? formatPace(paceValue) : `${formatPace(paceValue)} · GAP ${formatPace(gapValue)}`;
}

// Pace runs faster upwards (inverted), with the grade-adjusted line on top and the threshold pace line in hot.
function paceSeries(track: TrackData): LineSeriesOption[] {
  const line = { showSymbol: false, sampling: 'lttb' as const, connectNulls: false };
  const threshold = track.threshold ?? null;
  return [
    { type: 'line', name: 'PACE', data: track.points, ...line, lineStyle: { color: colors.ink, width: 1.2 }, itemStyle: { color: colors.ink } },
    {
      type: 'line', name: 'GRADE-ADJUSTED', data: track.gap ?? [], ...line, z: 3,
      lineStyle: { color: colors.sportRun, width: 2.2 }, itemStyle: { color: colors.sportRun },
      markLine: threshold === null ? undefined : {
        silent: true, symbol: 'none', label: { show: false }, lineStyle: { color: colors.hot, width: 1, type: 'solid' }, data: [{ yAxis: threshold }],
      },
    },
  ];
}

export function formatTrackValue(value: number | null, spec: TrackSpec): string {
  return orDash(value, (v) => (spec.kind === 'pace' ? formatPaceKm(v) : `${v.toFixed(spec.decimals)} ${spec.unit}`));
}

export function trackTooltip(spec: TrackSpec): (params: unknown) => string {
  return (params) => formatTrackValue(pointValue(params), spec);
}

const TIME_INTERVALS_S = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400] as const;
const MAX_TIME_TICKS = 6;
const SECONDS_LABEL_SPAN_S = 600;

export function pickTimeInterval(spanS: number): number {
  return TIME_INTERVALS_S.find((interval) => spanS / interval <= MAX_TIME_TICKS) ?? 14400;
}

// Labels only on interval multiples: ECharts also ticks the zoom window's raw edges, which would repeat a neighbour.
export function timeTickLabel(spanS: number): (seconds: number) => string {
  const interval = pickTimeInterval(spanS);
  const format = spanS < SECONDS_LABEL_SPAN_S ? formatMinutesSeconds : formatHoursMinutes;
  return (seconds) => (seconds % interval === 0 ? format(seconds) : '');
}

export function timeAxis(spanS: number) {
  return { interval: pickTimeInterval(spanS), axisLabel: { formatter: timeTickLabel(spanS) } };
}

type ZoomableChart = Pick<EChartsType, 'on' | 'off' | 'getWidth' | 'convertFromPixel' | 'setOption'>;

export function followZoomTicks(chart: ZoomableChart): void {
  chart.off('datazoom');
  chart.on('datazoom', () => {
    const start = Number(chart.convertFromPixel({ xAxisIndex: 0 }, 0));
    const end = Number(chart.convertFromPixel({ xAxisIndex: 0 }, chart.getWidth()));
    chart.setOption({ xAxis: timeAxis(end - start) });
  });
}

// Phone: two-finger pinch zooms and one-finger drags are left to the page scroll. Desktop zooms by drag-select only.
export function zoomOption(phone: boolean): DataZoomComponentOption {
  return {
    type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: phone, moveOnMouseMove: false, moveOnMouseWheel: false,
    ...(phone ? {} : { filterMode: 'none' as const }),
  };
}

export function streamTrackOption(track: TrackData, showAxis: boolean, maxT: number, variant: Variant = 'desktop'): ECOption {
  const { spec } = track;
  const phone = variant === 'phone';
  const time = timeAxis(maxT);
  return {
    // no outer-bounds shrinking + end-anchored labels keep every track's plot edges identical, so the synced crosshair lines up
    grid: { left: 0, right: 0, top: 4, bottom: showAxis ? 20 : 4, outerBoundsMode: 'none' },
    xAxis: {
      type: 'value', min: 0, max: maxT, interval: time.interval, splitLine: { show: false }, axisTick: { show: false }, axisLine: { show: false },
      axisLabel: { show: showAxis, alignMinLabel: 'left', alignMaxLabel: 'right', ...AXIS_TEXT, ...time.axisLabel },
    },
    yAxis: spec.kind === 'pace' ? { type: 'value', inverse: true, min: 'dataMin', max: 'dataMax', show: false } : { type: 'value', min: 0, show: false },
    toolbox: { show: true, itemSize: 0, feature: { dataZoom: { yAxisIndex: 'none', icon: { zoom: 'path://M0,0', back: 'path://M0,0' } } } },
    dataZoom: [zoomOption(phone)],
    tooltip: chartTooltip({
      phone, formatter: spec.kind === 'pace' ? paceTooltip : trackTooltip(spec), fontSize: 9, padding: [3, 6], axisPointer: { type: 'line', lineStyle: { color: colors.ink, width: 1.5 } },
    }),
    series: spec.kind === 'pace' ? paceSeries(track) : [{
      type: 'line', name: spec.name, data: track.points, showSymbol: false, sampling: 'lttb', connectNulls: false,
      lineStyle: { color: spec.line, width: 1.5 }, itemStyle: { color: spec.line },
      areaStyle: spec.area === null ? undefined : { color: spec.area },
    }],
  };
}

export function activateDragZoom(chart: Pick<EChartsType, 'dispatchAction'>): void {
  chart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: true });
}

export function resetZoom(chart: Pick<EChartsType, 'dispatchAction'>): void {
  chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
}
